import {
  ChangeEvent,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { t, Trans } from '@lingui/macro';

import Alert from '@decisiv/ui-components/lib/components/Alert';
import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { H3, P } from '@decisiv/ui-components/lib/components/Typography';

import Table from '@decisiv/iconix/lib/components/Table';
import Upload from '@decisiv/iconix/lib/components/Upload';

import { eventStopPropagation } from 'utils/eventStopPropagation';

import {
  stepOneSubmitRef,
  useBulkCreateDepotContext,
} from '../context/BulkCreateDepotContext';
import { useCreateDepotBulkImportMutation } from '../hooks/useCreateDepotBulkImportMutation';
import { downloadDepotTemplate, validateDepotCsv } from './csvTemplate';
import { HiddenFileInput, Row } from './styles';

const StepOne = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    savedState,
    setSavedState,
    setStepOneContinueButtonEnabled,
    setStepOneContinueButtonLoading,
  } = useBulkCreateDepotContext();

  const [uploadError, setUploadError] = useState<string | null>(null);

  const [createDepotBulkImport] = useCreateDepotBulkImportMutation();

  const selectedFile = savedState.file;

  const handleOnUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const rejectFile = useCallback(
    (message: string) => {
      setUploadError(message);
      setSavedState((prev) => ({ ...prev, file: undefined }));
      setStepOneContinueButtonEnabled(false);
    },
    [setSavedState, setStepOneContinueButtonEnabled],
  );

  const handleOnFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const file = input.files?.[0];
      // Clear the value so re-selecting the same file fires change again.
      input.value = '';
      if (!file) return;

      setUploadError(null);

      let validationError: string | null;
      try {
        validationError = await validateDepotCsv(file);
      } catch {
        rejectFile(t`We couldn't read that file. Please try again.`);
        return;
      }

      if (validationError) {
        rejectFile(validationError);
        return;
      }

      setSavedState((prev) => ({ ...prev, file }));
      setStepOneContinueButtonEnabled(true);
    },
    [rejectFile, setSavedState, setStepOneContinueButtonEnabled],
  );

  const handleSubmit = useCallback(async () => {
    // Defensive: Continue is disabled until a file is selected.
    /* istanbul ignore next -- @preserve */
    if (!selectedFile) return;

    setUploadError(null);
    setStepOneContinueButtonLoading(true);

    try {
      const { data } = await createDepotBulkImport({
        variables: { originalFile: selectedFile },
      });

      const importId = data?.createDepotBulkImport?.depotBulkImport?.id;

      if (!importId) {
        setUploadError(
          t`We were unable to start your upload. Please try again.`,
        );
        return;
      }

      // Setting the import id moves the wizard into its intermediate
      // (processing) state; the Summary step renders once polling resolves.
      setSavedState((prev) => ({ ...prev, importId }));
    } catch {
      setUploadError(t`We were unable to start your upload. Please try again.`);
    } finally {
      setStepOneContinueButtonLoading(false);
    }
  }, [
    createDepotBulkImport,
    selectedFile,
    setSavedState,
    setStepOneContinueButtonLoading,
  ]);

  useImperativeHandle(stepOneSubmitRef, () => ({ submit: handleSubmit }), [
    handleSubmit,
  ]);

  return (
    <Flex flexDirection="column">
      <H3>
        <Trans>Bulk Create Depots</Trans>
      </H3>
      <P size="small" shade={1} marginTop={0.5}>
        <Trans>
          Upload a CSV file to create multiple depots at once. Large files may
          take a few minutes to process.
        </Trans>
      </P>

      {uploadError && (
        <Flex marginTop={1}>
          <Alert intent="danger" title={t`Upload Failed`}>
            {uploadError}
          </Alert>
        </Flex>
      )}

      <Row>
        <Button
          icon={Upload}
          text={t`Upload CSV file`}
          size="small"
          kind="secondary"
          type="button"
          onClick={handleOnUploadClick}
        />
        <HiddenFileInput
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          hidden
          onChange={handleOnFileChange}
          onClick={eventStopPropagation}
        />
        <Button
          icon={Table}
          text={t`Download CSV Template`}
          size="small"
          type="button"
          variant="ghost"
          onClick={downloadDepotTemplate}
        />
      </Row>

      <P size="small" shade={1} marginTop={0.5}>
        <Trans>One DEPOT per row required.</Trans>
      </P>

      {selectedFile && (
        <P size="small" marginTop={1}>
          {selectedFile.name}
        </P>
      )}
    </Flex>
  );
};

export default StepOne;
